import { useCallback } from 'react';
import { useRouting } from 'hooks/useRouting';
import { Mode } from '../pages/TargetUpdateStep1';
import {
  PortfolioAssetType,
  TargetId,
  ManagementType,
  TargetType,
} from '../utils/type';

type TargetSupportRouting = {
  goTargetIndex: (isHistory?: boolean) => void;
  goTargetPushSelect: (isHistory?: boolean) => void;
  goTargetReplaceSelect: (isHistory?: boolean) => void;
  goTargetCreate: (type: TargetType, isHistory?: boolean) => void;
  goTargetRegister: (id: TargetId, isHistory?: boolean) => void;
  goSetTitle: (id: TargetId, isHistory?: boolean) => void;
  goSetAmount: (id: TargetId, isHistory?: boolean) => void;
  goTargetPushDetail: (id: TargetId, isHistory?: boolean) => void;
  goTargetReplaceDetail: (id: TargetId, isHistory?: boolean) => void;
  goTargetPushUpdateStep1: (
    id: TargetId,
    mode: Mode,
    isHistory?: boolean
  ) => void;
  goTargetReplaceUpdateStep1: (
    id: TargetId,
    mode: Mode,
    isHistory?: boolean
  ) => void;
  goTargetPushUpdateStep2: (
    id: TargetId,
    mode: Mode,
    isHistory?: boolean
  ) => void;
  goTargetReplaceUpdateStep2: (
    id: TargetId,
    mode: Mode,
    isHistory?: boolean
  ) => void;
  goTargetUpdateInfo: (id: TargetId, mode: Mode, isHistory?: boolean) => void;
  goTargetPushUpdateConfirm: (
    id: TargetId,
    mode: Mode,
    isHistory?: boolean
  ) => void;
  goTargetReplaceUpdateConfirm: (
    id: TargetId,
    mode: Mode,
    isHistory?: boolean
  ) => void;
  goSetHowToSave: (id: TargetId, mode: Mode, isHistory?: boolean) => void;
  goSetHowToSaveSetAmount: (
    id: TargetId,
    mode: Mode,
    isHistory?: boolean
  ) => void;
  goSetHowToSaveSetAccount: (
    id: TargetId,
    mode: Mode,
    isHistory?: boolean
  ) => void;
  goSetHowToSaveSetExceptAmount: (
    id: TargetId,
    mode: Mode,
    isHistory?: boolean
  ) => void;
  goPortfolioAnalysis: (id: TargetId, isHistory?: boolean) => void;
  goHowToIncreaseAccumulationAmount: (
    id: TargetId,
    isHistory?: boolean
  ) => void;
  goWhatIsNecessaryYield: (id: TargetId, isHistory?: boolean) => void;
  goPortfolioAnalysisAssets: (
    id: TargetId,
    type: PortfolioAssetType,
    isHistory?: boolean
  ) => void;
  goPortfolioAnalysisChangeAssets: (id: TargetId, isHistory?: boolean) => void;
  goPortfolioAnalysisChangeAssetsConfirm: (
    id: TargetId,
    managementType: ManagementType,
    isHistory?: boolean
  ) => void;
  goPortfolioAnalysisChangeAssetsComplete: (
    id: TargetId,
    managementType: ManagementType,
    isHistory?: boolean
  ) => void;
};

export const useTargetSupportRouting = (): TargetSupportRouting => {
  const { pushRouting, replaceRouting } = useRouting();

  const goTargetIndex = useCallback(
    (isHistory = true) => {
      pushRouting({ path: `/target` }, isHistory);
    },
    [pushRouting]
  );

  const goTargetPushSelect = useCallback(
    (isHistory = true) => {
      pushRouting({ path: `/target/select` }, isHistory, window.scrollY);
    },
    [pushRouting]
  );

  const goTargetReplaceSelect = useCallback(
    (isHistory = true) => {
      replaceRouting({ path: `/target/select` }, isHistory);
    },
    [replaceRouting]
  );

  const goTargetCreate = useCallback(
    (type: TargetType, isHistory = true) => {
      pushRouting(
        { path: `/target/create/${type}` },
        isHistory,
        window.scrollY
      );
    },
    [pushRouting]
  );

  const goTargetRegister = useCallback(
    (id: TargetId, isHistory = true) => {
      pushRouting({ path: `/target/${id}/register` }, isHistory);
    },
    [pushRouting]
  );

  const goSetTitle = useCallback(
    (id: TargetId, isHistory = true) => {
      pushRouting({ path: `/target/${id}/set-title` }, isHistory);
    },
    [pushRouting]
  );

  const goSetAmount = useCallback(
    (id: TargetId, isHistory = true) => {
      pushRouting({ path: `/target/${id}/set-amount` }, isHistory);
    },
    [pushRouting]
  );

  const goTargetPushDetail = useCallback(
    (id: TargetId, isHistory = true) => {
      pushRouting({ path: `/target/${id}` }, isHistory, window.scrollY);
    },
    [pushRouting]
  );

  const goTargetReplaceDetail = useCallback(
    (id: TargetId, isHistory = true) => {
      replaceRouting({ path: `/target/${id}` }, isHistory);
    },
    [replaceRouting]
  );

  const goTargetPushUpdateStep1 = useCallback(
    (id: TargetId, mode: Mode, isHistory = true) => {
      pushRouting(
        { path: `/target/${id}/update/step1/${mode}` },
        isHistory,
        window.scrollY
      );
    },
    [pushRouting]
  );

  const goTargetReplaceUpdateStep1 = useCallback(
    (id: TargetId, mode: Mode, isHistory = true) => {
      replaceRouting({ path: `/target/${id}/update/step1/${mode}` }, isHistory);
    },
    [replaceRouting]
  );

  const goTargetPushUpdateStep2 = useCallback(
    (id: TargetId, mode: Mode, isHistory = true) => {
      pushRouting(
        { path: `/target/${id}/update/step2/${mode}` },
        isHistory,
        window.scrollY
      );
    },
    [pushRouting]
  );

  const goTargetReplaceUpdateStep2 = useCallback(
    (id: TargetId, mode: Mode, isHistory = true) => {
      replaceRouting({ path: `/target/${id}/update/step2/${mode}` }, isHistory);
    },
    [replaceRouting]
  );

  const goTargetUpdateInfo = useCallback(
    (id: TargetId, mode: Mode, isHistory = true) => {
      pushRouting(
        { path: `/target/${id}/update/info/${mode}` },
        isHistory,
        window.scrollY
      );
    },
    [pushRouting]
  );

  const goTargetPushUpdateConfirm = useCallback(
    (id: TargetId, mode: Mode, isHistory = true) => {
      pushRouting(
        { path: `/target/${id}/update/confirm/${mode}` },
        isHistory,
        window.scrollY
      );
    },
    [pushRouting]
  );

  const goTargetReplaceUpdateConfirm = useCallback(
    (id: TargetId, mode: Mode, isHistory = true) => {
      replaceRouting(
        { path: `/target/${id}/update/confirm/${mode}` },
        isHistory
      );
    },
    [replaceRouting]
  );

  const goSetHowToSave = useCallback(
    (id: TargetId, mode: Mode, isHistory = true) => {
      pushRouting(
        { path: `/target/${id}/set-how-to-save/${mode}` },
        isHistory,
        window.scrollY
      );
    },
    [pushRouting]
  );

  const goSetHowToSaveSetAmount = useCallback(
    (id: TargetId, mode: Mode, isHistory = true) => {
      pushRouting(
        { path: `/target/${id}/set-how-to-save/${mode}/set-amount` },
        isHistory,
        window.scrollY
      );
    },
    [pushRouting]
  );

  const goSetHowToSaveSetAccount = useCallback(
    (id: TargetId, mode: Mode, isHistory = true) => {
      pushRouting(
        { path: `/target/${id}/set-how-to-save/${mode}/set-account` },
        isHistory,
        window.scrollY
      );
    },
    [pushRouting]
  );

  const goSetHowToSaveSetExceptAmount = useCallback(
    (id: TargetId, mode: Mode, isHistory = true) => {
      pushRouting(
        { path: `/target/${id}/set-how-to-save/${mode}/set-exceptamount` },
        isHistory,
        window.scrollY
      );
    },
    [pushRouting]
  );

  const goPortfolioAnalysis = useCallback(
    (id: TargetId, isHistory = true) => {
      pushRouting(
        { path: `/target/${id}/portfolio-analysis` },
        isHistory,
        window.scrollY
      );
    },
    [pushRouting]
  );

  const goHowToIncreaseAccumulationAmount = useCallback(
    (id: TargetId, isHistory = true) => {
      pushRouting(
        { path: `/target/${id}/howToIncreaseAccumulationAmount` },
        isHistory,
        window.scrollY
      );
    },
    [pushRouting]
  );

  const goWhatIsNecessaryYield = useCallback(
    (id: TargetId, isHistory = true) => {
      pushRouting(
        { path: `/target/${id}/whatIsNecessaryYield` },
        isHistory,
        window.scrollY
      );
    },
    [pushRouting]
  );

  const goPortfolioAnalysisAssets = useCallback(
    (id: TargetId, type: PortfolioAssetType, isHistory = true) => {
      pushRouting(
        { path: `/target/${id}/portfolio-analysis/asset/${type}` },
        isHistory,
        window.scrollY
      );
    },
    [pushRouting]
  );

  const goPortfolioAnalysisChangeAssets = useCallback(
    (id: TargetId, isHistory = true) => {
      pushRouting(
        { path: `/target/${id}/portfolio-analysis/change-assets` },
        isHistory,
        window.scrollY
      );
    },
    [pushRouting]
  );

  const goPortfolioAnalysisChangeAssetsConfirm = useCallback(
    (id: TargetId, managementType: ManagementType, isHistory = true) => {
      pushRouting(
        {
          path: `/target/${id}/portfolio-analysis/change-assets/confirm/${managementType}`,
        },
        isHistory,
        window.scrollY
      );
    },
    [pushRouting]
  );

  const goPortfolioAnalysisChangeAssetsComplete = useCallback(
    (id: TargetId, managementType: ManagementType, isHistory = true) => {
      pushRouting(
        {
          path: `/target/${id}/portfolio-analysis/change-assets/complete/${managementType}`,
        },
        isHistory,
        window.scrollY
      );
    },
    [pushRouting]
  );

  return {
    goTargetIndex,
    goTargetPushSelect,
    goTargetReplaceSelect,
    goTargetCreate,
    goTargetRegister,
    goSetTitle,
    goSetAmount,
    goTargetPushDetail,
    goTargetReplaceDetail,
    goTargetPushUpdateStep1,
    goTargetReplaceUpdateStep1,
    goTargetPushUpdateStep2,
    goTargetReplaceUpdateStep2,
    goTargetUpdateInfo,
    goTargetPushUpdateConfirm,
    goTargetReplaceUpdateConfirm,
    goSetHowToSave,
    goSetHowToSaveSetAmount,
    goSetHowToSaveSetAccount,
    goSetHowToSaveSetExceptAmount,
    goPortfolioAnalysis,
    goHowToIncreaseAccumulationAmount,
    goWhatIsNecessaryYield,
    goPortfolioAnalysisAssets,
    goPortfolioAnalysisChangeAssets,
    goPortfolioAnalysisChangeAssetsConfirm,
    goPortfolioAnalysisChangeAssetsComplete,
  };
};
